<template>
  <div class="visitor-photo-root">
    <!-- Settings first in DOM so Tab reaches capture device controls before the large photo control (2189122). -->
    <VisitorCaptureViaMenu
      @notify="$emit('notify', $event)"
      @device-select="onDeviceSelect"
    />
    <div v-if="data.form !== null">
      <button
        v-if="hasNoProfileImageAndImageData"
        type="button"
        class="visitor-photo-open-trigger visitor-check-in-photo-container d-flex flex-column align-items-center justify-content-center"
        aria-labelledby="visitor-take-photo-visible-label"
        :aria-describedby="takePhotoHintId"
        @click="openModal"
      >
        <span
          id="visitor-take-photo-visible-label"
          class="d-inline-flex flex-column align-items-center"
        >
          <i
            v-if="!isPhotoCaptureDeviceApp"
            class="ri-camera-2-line"
            aria-hidden="true"
          ></i>
          <i v-else class="ri-send-plane-fill" aria-hidden="true"></i>
          {{ isPhotoCaptureDeviceApp ? "Send capture request" : "Take photo" }}
        </span>
        <span :id="takePhotoHintId" class="visually-hidden">{{
          takePhotoTooltip
        }}</span>
      </button>
      <button
        v-else
        type="button"
        class="visitor-photo-open-trigger visitor-check-in-captured-image-container"
        :aria-label="takePhotoTooltip"
        @click="openModal"
      >
        <img
          :src="data?.form?.imageData || data?.form?.profileImage || ''"
          class="visitor-check-in-captured-image"
          alt=""
        />
        <i
          v-if="!isPhotoCaptureDeviceApp"
          class="ri-camera-2-line visitor-check-in-captured-image-icon"
          aria-hidden="true"
        ></i>
        <i
          v-else
          class="ri-send-plane-fill visitor-check-in-captured-image-icon"
          aria-hidden="true"
        ></i>
      </button>
    </div>
  </div>
</template>

<script>
import VisitorCaptureViaMenu from "@/v3components/Forms/Visitor/CheckIns/VisitorCaptureViaMenu.vue"
import {
  defineAsyncComponent,
  inject,
  ref,
  computed,
  reactive,
  onMounted,
  watch
} from "vue"
import visitorConstants from "@/constants/visitorConstants"
const VisitorPhotoCapturePopUp = defineAsyncComponent(
  () =>
    import("@/v3components/Forms/Visitor/CheckIns/VisitorPhotoCapturePopUp.vue")
)

export default {
  name: "VisitorTakePhoto",
  components: {
    VisitorCaptureViaMenu
  },
  props: {
    form: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["capture", "notify"],
  setup(props, { emit }) {
    const modal = inject("modal")
    const imageData = ref(null)
    const photoCaptureDevice = ref("")
    /** Stable id for hint text; avoids duplicate `id` on the two branches (Vue keeps one span in DOM). */
    const takePhotoHintId = `visitor-take-photo-hint-${Math.random()
      .toString(36)
      .slice(2, 11)}`
    const data = reactive({
      form: null
    })

    const hasProfileImage = computed(() => {
      return props?.form?.profileImage?.trim()?.length > 0
    })

    const hasImageData = computed(() => {
      return !!props?.form?.imageData
    })

    const hasNoProfileImageAndImageData = computed(() => {
      return !hasProfileImage.value && !hasImageData.value
    })

    const isPhotoCaptureDeviceApp = computed(() => {
      return [
        visitorConstants.VISITOR_CAMERA_TYPES.IOS,
        visitorConstants.VISITOR_CAMERA_TYPES.ANDROID
      ].includes(photoCaptureDevice.value)
    })

    const takePhotoTooltip = computed(() => {
      return isPhotoCaptureDeviceApp.value
        ? "Click to send photo capture request to the app"
        : "Click to take photo"
    })

    const openModal = () => {
      if (isPhotoCaptureDeviceApp.value) {
        emit("notify", photoCaptureDevice.value)
        return
      }

      modal.open(VisitorPhotoCapturePopUp, {
        size: "sm",
        title: visitorConstants.CHECK_IN_FORM.MODAL_TITLE_TAKE_PHOTO,
        props: {
          onCapture: (photoData) => {
            imageData.value = photoData
            emit("capture", photoData)
          }
        }
      })
    }

    const onDeviceSelect = (device) => {
      photoCaptureDevice.value = device
    }

    onMounted(() => {
      data.form = props.form
    })

    watch(
      () => props.form,
      (value) => {
        data.form = value
      },
      { deep: true }
    )

    return {
      hasNoProfileImageAndImageData,
      takePhotoTooltip,
      takePhotoHintId,
      isPhotoCaptureDeviceApp,
      data,
      openModal,
      onDeviceSelect
    }
  }
}
</script>

<style scoped>
/*
 * Native <button> for a11y only — do not override visitor.scss
 * (.visitor-check-in-photo-container / .visitor-check-in-captured-image-container):
 * border, size, flex layout, and colors must match the original div design.
 */
.visitor-photo-open-trigger {
  margin: 0;
  padding: 0;
  /* Do not set appearance:none: WCAG 2189116 — some SR/Deque paths drop native button role. */
  font: inherit;
  color: inherit;
  text-align: inherit;
  box-sizing: border-box;
}
</style>
